import {
  BEST_SELLERS,
  CHATBOT,
  FORECAST_REPORT,
  HELP,
  IMPORT_INCOMING_INVENTORY,
  INCOMING_INVENTORY,
  OVERSTOCK,
  PROFILE,
  ROOT,
} from "../navigation/CONSTANTS";

export const APP_WINDOW_QUERY_PARAM = "appWindow";
export const APP_WINDOW_QUERY_VALUE = "1";
export const APP_WINDOW_ELEMENT_ID = "fullscreen-window";

/*
 * Shopify App Window loads a second copy of this SPA in an overlay iframe.
 * Detect that on the first load of this JS realm and keep the answer, so
 * in-app navigations that drop ?appWindow=1 still stay in overlay mode.
 *
 * Example first-load URL: "/ui/bestsellers?appWindow=1" → true for this iframe
 * Example parent Admin iframe: "/ui/" → false for this iframe
 */
let detectedAppWindowOnLoad = null;

const hasAppWindowOpener = () => {
  try {
    return Boolean(window.opener && window.opener !== window);
  } catch (err) {
    return false;
  }
};

export const isInAppWindow = () => {
  if (typeof window === "undefined") {
    return false;
  }
  if (detectedAppWindowOnLoad === null) {
    const params = new URLSearchParams(window.location.search);
    detectedAppWindowOnLoad =
      params.get(APP_WINDOW_QUERY_PARAM) === APP_WINDOW_QUERY_VALUE ||
      hasAppWindowOpener();
  }
  return detectedAppWindowOnLoad;
};

/*
 * Adds or keeps appWindow=1 on a query string.
 * Example: "goto=1" → "goto=1&appWindow=1"
 */
export const withAppWindowQuery = (search = "") => {
  const raw =
    typeof search === "string" ? search.replace(/^\?/, "") : String(search || "");
  const params = new URLSearchParams(raw);
  params.set(APP_WINDOW_QUERY_PARAM, APP_WINDOW_QUERY_VALUE);
  return params.toString();
};

/*
 * Overlay iframe URL for the current parent route.
 * Example: "/ui/settings?goto=3" → "/ui/settings?goto=3&appWindow=1"
 */
export const getAppWindowSrc = (location = window.location) => {
  const search = withAppWindowQuery(location.search || "");
  return `${location.pathname}?${search}${location.hash || ""}`;
};

export const getAppWindowHeading = (pathname = "") => {
  if (pathname.startsWith(BEST_SELLERS)) {
    return "Best Sellers";
  }
  if (pathname.startsWith(OVERSTOCK)) {
    return "Overstock";
  }
  if (
    pathname.startsWith(IMPORT_INCOMING_INVENTORY) ||
    pathname.startsWith(INCOMING_INVENTORY)
  ) {
    return "Incoming Inventory";
  }
  if (pathname.startsWith(PROFILE)) {
    return "Settings";
  }
  if (pathname.startsWith(HELP)) {
    return "Help";
  }
  if (pathname.startsWith(CHATBOT)) {
    return "Chat";
  }
  if (pathname === FORECAST_REPORT) {
    return "Forecast Report";
  }
  if (pathname === ROOT) {
    return "Dashboard";
  }
  return "Dashboard";
};

export const APP_WINDOW_CHANNEL = "ifh-app-window";
export const APP_WINDOW_HIDE_MESSAGE = "hide";

const getAppWindowElement = () => document.getElementById(APP_WINDOW_ELEMENT_ID);

/*
 * Opens Shopify's App Window overlay. Must only run from a merchant click,
 * never on page load or after auth.
 */
export const showAppWindow = async () => {
  const win = getAppWindowElement();
  if (!win || typeof win.show !== "function") {
    return;
  }
  win.src = getAppWindowSrc();
  await win.show();
};

/*
 * Closes the overlay. The s-app-window element lives in the parent iframe,
 * so the overlay copy of the SPA asks it to hide over a same-origin channel.
 *
 * Example: overlay click → { type: "hide" } → parent calls win.hide()
 */
export const hideAppWindow = async () => {
  const win = getAppWindowElement();
  if (win && typeof win.hide === "function") {
    await win.hide();
    return;
  }
  try {
    const channel = new BroadcastChannel(APP_WINDOW_CHANNEL);
    channel.postMessage({ type: APP_WINDOW_HIDE_MESSAGE });
    channel.close();
  } catch (err) {
    console.error("Cannot exit full screen", err);
  }
};
